// ABOUTME: Utility function to fetch all applications data for export
// ABOUTME: Fetches complete dataset not just visible rows

import { IGetAllDashboardRowsParams } from '../../../store/offers-dashboard/types';
import { TableRow } from '../../organisms/ApplyDataGrid/table/types';
import { AuthState } from '../../../store/auth/types';
import { IIntakeIdWithDates, ApplicationStatus, ApplicantOfferStatus, FilterKey } from 'api-paths-cas';
import {
  getIdleParamsFromParams,
  getUniversityFromParams,
  getFilterKeysFromParams,
} from '../../../store/offers-dashboard/sagas/helpers';
import { isAgentOrAgencyRole, isPartnerRole } from '../../../utilities/roles/permissions';
import { INTAKE_ALL_START_DATE, INTAKE_ALL_END_DATE } from '../../../utilities/constants';
import { QueryKeys } from '../../../types';
import { MATCH_FILTER_NAMES } from 'shared-components';
import { queryDatabaseForApplicantOffers, queryDatabaseForApplications } from '@/api/api';
import { serialiseDashboardEntiresToDashboardRows } from '@/components/organisms/ApplyDataGrid/table/rows';

const MAX_PAGES = 100; // Safety limit to prevent infinite loops
const ROWS_PER_PAGE = 1000; // Fetch 1000 rows at a time

/**
 * Creates parameters for fetching all dashboard rows for export
 */
const createExportParams = ({
  auth,
  intakeId,
  intakeRange,
  idToken,
}: {
  auth: AuthState;
  intakeId: string;
  intakeRange: IIntakeIdWithDates | undefined;
  idToken: string;
}): IGetAllDashboardRowsParams | undefined => {
  const params = new URLSearchParams(window.location.search);

  const uni = getUniversityFromParams(auth, params);

  const isAgent = isAgentOrAgencyRole(auth.role);
  const isPartner = isPartnerRole(auth.role);

  const startDate = (isAgent || isPartner) && (intakeRange?.startDate ?? INTAKE_ALL_START_DATE);
  const endDate = (isAgent || isPartner) && (intakeRange?.endDate ?? INTAKE_ALL_END_DATE);

  const filters = getFilterKeysFromParams(params);
  const { idleFrom, idleTo } = getIdleParamsFromParams(params);

  const agentId = auth.agencyId;
  const officeId = auth.currentUser.agencyOffice;
  const currentUserRegionId = auth.currentUser?.regionAccessId;

  const defaultStages = {
    applicationStages: [
      ApplicationStatus.InProgress,
      ApplicationStatus.Completed,
      ApplicationStatus.Rejected,
      ApplicationStatus.Withdrawn,
    ],
    offerStages: [
      ApplicantOfferStatus.Applied,
      ApplicantOfferStatus.Conditional,
      ApplicantOfferStatus.Unconditional,
      ApplicantOfferStatus.Completed,
      ApplicantOfferStatus.Rejected,
      ApplicantOfferStatus.Withdrawn,
      ApplicantOfferStatus.Rejected,
      ApplicantOfferStatus.Withdrawn,
    ],
  };

  // TODO: Left in case by-stage export will be needed in the future
  // let appStages = params.getAll(QueryKeys.APPLICATION_STAGE);
  //let offerStages = params.getAll(QueryKeys.OFFER_STAGE);

  const appStages = defaultStages.applicationStages;
  const { offerStages } = defaultStages;

  return {
    abortController: new AbortController(),
    idToken,
    startDate,
    endDate,

    appStages,
    offerStages,

    intakeId,
    uni,
    //page, // Use the page parameter passed in
    pageSize: ROWS_PER_PAGE,
    sortCol: params.get(QueryKeys.SORT_COL),
    sortDir: params.get(QueryKeys.SORT_DIR),
    search: params.get(QueryKeys.SEARCH),

    // Filters
    filters,

    // office:
    agentId,
    officeId,
    currentUserRegionId,

    // Programmable tags:
    tag: params.getAll(FilterKey.TAGS),
    programmableTag: params.getAll(FilterKey.PROGRAMMABLE_TAGS),
    tagsMatch: params.getAll(MATCH_FILTER_NAMES.TAGS_MATCH),
    programmableTagsMatch: params.getAll(MATCH_FILTER_NAMES.PROGRAMMABLE_TAGS_MATCH),

    idleFrom,
    idleTo,
  } as IGetAllDashboardRowsParams;
};

interface IGetAllApplicationsForExportParams {
  auth: AuthState;
  intakeId: string;
  intakeRange: IIntakeIdWithDates | undefined;
  idToken: string;
}

/**
 * Fetches all applications data for export (not paginated)
 */
export const getAllApplicationsForExport = async (params: IGetAllApplicationsForExportParams): Promise<TableRow[]> => {
  const exportParams = createExportParams(params);

  const applicantOfferTableRowsPromise = runApiCall(exportParams, async (data: IGetAllDashboardRowsParams) => {
    const res = await queryDatabaseForApplicantOffers(data, data.offerStages);
    return serialiseDashboardEntiresToDashboardRows(res);
  });

  const applicationTableRowsPromise = runApiCall(exportParams, async (data: IGetAllDashboardRowsParams) => {
    const res = await queryDatabaseForApplications(data, data.appStages);
    return serialiseDashboardEntiresToDashboardRows(res);
  });

  const [applicationTableRows, applicantOfferTableRows] = await Promise.all([
    applicationTableRowsPromise,
    applicantOfferTableRowsPromise,
  ]);

  return [...applicationTableRows, ...applicantOfferTableRows];
};

async function runApiCall(
  exportParams: IGetAllDashboardRowsParams | undefined,
  callBack: (data: IGetAllDashboardRowsParams) => Promise<TableRow[]>,
): Promise<TableRow[]> {
  const allRows: TableRow[] = [];
  let currentPage = 1;
  let hasMoreData = true;

  try {
    // Fetch data page by page
    while (hasMoreData && currentPage <= MAX_PAGES) {
      if (!exportParams) {
        console.warn('Failed to create export parameters');
        break;
      }

      // Fetch the current page of data
      const pageRows = await callBack({ ...exportParams, page: currentPage });

      if (pageRows && pageRows.length > 0) {
        allRows.push(...pageRows);

        // If we got fewer rows than expected, we've reached the end
        if (pageRows.length < ROWS_PER_PAGE) {
          hasMoreData = false;
        } else {
          currentPage++;
        }
      } else {
        // No more data available
        hasMoreData = false;
      }
    }

    console.log(`Fetched ${allRows.length} total rows for export across ${currentPage} pages`);
    return allRows;
  } catch (error) {
    console.error('Error fetching applications for export:', error);
    throw error;
  }
}
